import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Stack, Typography, Box } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { paths } from 'routes';

import { Search } from 'components/Search';
import { BreadCrumbs } from 'components/Breadcrumbs';
import { ContentLayout } from 'components/Layout';
import { NotFound } from 'components/NotFound/NotFound';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';

//import { Add } from '../components/Add';
import { useAuth } from 'lib/auth';

import { Loader } from 'components/Loader';
import { DetailViewTabList } from 'components/TabList';

import { useGetCompanyUsers } from '../api/getCompanyUsers';
import { useCompanyUsersCount } from '../api/getCompanyUsersCount';
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal';
import { InviteUserModal } from '../components/InviteUserModal';
import { useRemoveUser } from '../api/removeUser';
import { useHandleUserRequest } from '../api/handleUserRequest';
import { UserListItem } from '../components/UsersListItem';
import { useGetCompanyById } from '../api/getCompanyById';

const STATUS_TABS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Invited', value: 'invited' },
  { label: 'Requested Access', value: 'access_requested' },
];

export const UsersList = ({ companyType }) => {
  const { id: companyID } = useParams();
  const { account, user } = useAuth();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [currentTab, setCurrentTab] = useState('active');
  const [search, setSearch] = useState('');
  const [markedForDelete, setMarkedForDelete] = useState(null);
  const [markedForAccept, setMarkedForAccept] = useState(null);
  const [markedForReject, setMarkedForReject] = useState(null);
  const [markedForRemind, setMarkedForRemind] = useState(null);
  const [markedForWithdraw, setMarkedForWithdraw] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(null);

    const { data: company } = useGetCompanyById(companyID);

  const {
      data: { users, total },
      refetch: refetchUsers,
      isFetching,
    } = useGetCompanyUsers(companyID, {
      search,
      status: currentTab,
      page: page - 1,
      limit: pageSize,
      queryOptions: {
        onSettled: () => {
          clearTimeout(timer);
          setTimeout(() => setLoading(false), 1000);
        },
      },
    });

    const {
      //@ts-ignore
      data: { count: activeCount },
      refetch: refetchActiveCount,
    } = useCompanyUsersCount(companyID, { status: 'active' });

    const {
      //@ts-ignore
      data: { count: inactiveCount },
      refetch: refetchInactiveCount,
    } = useCompanyUsersCount(companyID, { status: 'inactive' });

    const {
      //@ts-ignore
      data: { count: invitedCount },
      refetch: refetchInvitedCount,
    } = useCompanyUsersCount(companyID, { status: 'invited' });

    const {
      //@ts-ignore
      data: { count: requestedAccessCount },
      refetch: refetchRequestedAccessCount,
    } = useCompanyUsersCount(companyID, { status: 'access_requested' });

  const refetchAllCounts = () => {
    refetchActiveCount();
    refetchInactiveCount();
    refetchInvitedCount();
    refetchRequestedAccessCount();
  };

  const debouncedSearchChange = useDebouncedCallback((search) => setSearch(search), 300);
  const handleSearchChange = ({ target: { value: search } }) => {
    debouncedSearchChange(search);
  };
  const handlePageChange = (_, page) => setPage(page);
  const handleTabChange = (_, newTab) => {
    setPage(1);
    setCurrentTab(newTab);
  };

  const labelName = companyType === 'buyer' ? 'Buyers' : 'Suppliers';

  const onUserRemoval = () => {
    refetchUsers();
    setMarkedForDelete(null);
    refetchAllCounts();
  };

  const onUserAcceptance = () => {
    refetchUsers();
    setMarkedForAccept(null);
    refetchAllCounts();
  };

  const onUserRejecting = () => {
    refetchUsers();
    setMarkedForReject(null);
    refetchAllCounts();
  };

  const onUserInvite = () => {
    refetchUsers();
    refetchAllCounts();
  };

  const onUserRemind = () => {
    refetchUsers();
    setMarkedForRemind(null);
    refetchAllCounts();
  };

  const onUserWithdraw = () => {
    refetchUsers();
    setMarkedForWithdraw(null);
    refetchAllCounts();
  };

  const { mutateAsync: removeUser } = useRemoveUser(onUserRemoval);
  const { mutateAsync: handleAcceptRequest } = useHandleUserRequest(onUserAcceptance, 'User successfully accepted');
  const { mutateAsync: handleRejectRequest } = useHandleUserRequest(onUserRejecting, 'User successfully rejected');
  const { mutateAsync: handleRemindRequest } = useHandleUserRequest(onUserRemind, 'User successfully reminded');
  const { mutateAsync: handleWithdrawRequest } = useHandleUserRequest(onUserWithdraw, 'User successfully withdrawn');
  
  const companyAdmin = (user.role === 'company_admin' && user.Company.company_id === parseInt(companyID));

  useUsersRefetch();

  useEffect(() => {
    if (isFetching) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isFetching]);

  return (
    <ContentLayout>
      <Stack height={1}>
        <Stack>
          <BreadCrumbs
            items={[
              { name: '', to: paths.homepage },
              { name: company?.company?.Name, to: paths.suppliersDetailsView(companyID) },
              { name: `Users${total > 0 ? ` (${total})` : ''}` },
            ]}
          />
        </Stack>

        <Box
          sx={{
            height: 16,
            backgroundColor: 'background.paper',
          }}
        />

        <DetailViewTabList
          value={currentTab}
          onChange={handleTabChange}
          tabs={[
            { label: STATUS_TABS[0].label, value: STATUS_TABS[0].value, count: activeCount },
            { label: STATUS_TABS[1].label, value: STATUS_TABS[1].value, count: inactiveCount },
            { label: STATUS_TABS[2].label, value: STATUS_TABS[2].value, count: invitedCount },
            { label: STATUS_TABS[3].label, value: STATUS_TABS[3].value, count: requestedAccessCount },
          ]}
        />

        <Stack
          px={4}
          py={2}
          bgcolor="background.paper"
          borderTop="1px solid"
          borderColor="border.main"
          height="100%"
        >
          {(account.super_admin || companyAdmin) && (
            <>
            <DeleteConfirmationModal
              title={'Remove User'}
              markedForDelete={markedForDelete}
              mutateAsync={() => removeUser({ userID: markedForDelete?.itemId })
              }
            >
              <Typography variant="body1" fontWeight={500}>
                Please confirm the removal of the user{' '}
                <span style={{ fontWeight: 'bold' }}>{markedForDelete?.name}.</span>
              </Typography>
            </DeleteConfirmationModal>
            <DeleteConfirmationModal
              title={'Accept User'}
              markedForDelete={markedForAccept}
              mutateAsync={() => handleAcceptRequest({ companyID:companyID, userID: markedForAccept?.itemId, action: "accept"})
              }
            >
              <Typography variant="body1" fontWeight={500}>
                Are you sure you want to accept user{' '}
                <span style={{ fontWeight: 'bold' }}>{markedForAccept?.name}?</span>
              </Typography>
            </DeleteConfirmationModal>
            <DeleteConfirmationModal
              title={'Accept User'}
              markedForDelete={markedForReject}
              mutateAsync={() => handleRejectRequest({ companyID:companyID, userID: markedForReject?.itemId, action: "reject"})
              }
            >
              <Typography variant="body1" fontWeight={500}>
                Are you sure you want to reject user{' '}
                <span style={{ fontWeight: 'bold' }}>{markedForReject?.name}?</span>
              </Typography>
            </DeleteConfirmationModal>
            <DeleteConfirmationModal
              title={'Remind User'}
              markedForDelete={markedForRemind}
              mutateAsync={() => handleRemindRequest({ companyID:companyID, userID: markedForRemind?.itemId, action: "remind"})
              }
            >
              <Typography variant="body1" fontWeight={500}>
                Are you sure you want to remind user{' '}
                <span style={{ fontWeight: 'bold' }}>{markedForRemind?.name}?</span>
              </Typography>
            </DeleteConfirmationModal>
            <DeleteConfirmationModal
              title={'Withdraw User'}
              markedForDelete={markedForWithdraw}
              mutateAsync={() => handleWithdrawRequest({ companyID:companyID, userID: markedForWithdraw?.itemId, action: "withdraw"})
              }
            >
              <Typography variant="body1" fontWeight={500}>
                Are you sure you want to withdraw user{' '}
                <span style={{ fontWeight: 'bold' }}>{markedForWithdraw?.name}?</span>
              </Typography>
            </DeleteConfirmationModal>
            </>
          )}
          <Stack direction="row" gap={2} alignItems="center">
            <Search handleSearchChange={handleSearchChange} sx={{ minWidth: 200, width: 270 }} />
            <Box sx={{ flex: 1 }} />
            {(account.super_admin || companyAdmin) && (
              <Box sx={{minWidth: 150, width: 200}}>
                <InviteUserModal handleSuccess={onUserInvite} title={"Invite User"} />
              </Box>
            )}
          </Stack>

          {isFetching && !loading ? 
          (
            <></>
          ) :
          (!isFetching || (isFetching && loading)) ?
          (
            <>
            {loading && (
              <Loader />
            )}

            {!loading && (
              <>
                <Stack gap={1} flex={1}>
                  {users?.length === 0 && (
                    <NotFound
                      title="No related users found"
                      caption={
                        search
                          ? 'Please try another search term'
                          : account.super_admin ? 'Click the "Invite User" button to begin' : ''
                      }
                      wrapperSx={{ mt: 19 }}
                    />
                  )}
                  {users.map((user) => (
                    <UserListItem
                      key={user.user_id}
                      markForDelete={() => setMarkedForDelete({ name: `${user.first_name} ${user.last_name}`, itemId: user.user_id })}
                      markForAccept={() => setMarkedForAccept({ name: `${user.first_name} ${user.last_name}`, itemId: user.user_id })}
                      markForReject={() => setMarkedForReject({ name: `${user.first_name} ${user.last_name}`, itemId: user.user_id })}
                      markForRemind={() => setMarkedForRemind({ name: `${user.first_name} ${user.last_name}`, itemId: user.user_id })}
                      markForWithdraw={() => setMarkedForWithdraw({ name: `${user.first_name} ${user.last_name}`, itemId: user.user_id })}
                      companyID={parseInt(companyID)}
                      {...user}
                      onUserInvite={onUserInvite}
                      companyAdmin={companyAdmin}
                    />
                  ))}
                </Stack>

                <Stack direction="row" justifyContent="center">
                  <Pagination
                    page={page}
                    handlePageChange={handlePageChange}
                    itemCount={total}
                    itemsPerPage={pageSize}
                    onItemsPerPageChange={setPageSize}
                  />
                </Stack>
                </>
            )}
            </>
          ) :
          (
            <></>
          )}
        </Stack>
        
      </Stack>
    </ContentLayout>
  );

  function useUsersRefetch() {
    useEffect(() => {
      refetchUsers();
    }, [page, pageSize, currentTab, search]);
  }
};
